import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/libs/utils";
import { Button } from "@/v2-components/ui/button";
import ComponentErrorBoundary from "@/v2-components/pages/ComponentErrorBoundary";
import { useTrans } from "@/utils/useTrans";

const PRODUCTS_KEYS = [
  {
    recommended: true,
    name: "TactSuit Pro",
    taglineKey: "live_products_pro_tagline",
    points: 32,
    pointsCoverKey: "live_products_pro_cover",
    descriptionKey: "live_products_pro_desc",
    imageSrc: "/images/software/live/products-pro.png",
    mobileImageSrc: "/images/software/live/products-pro-mobile.png",
    specValues: [
      "live_products_pro_spec_1_value",
      "live_products_pro_spec_2_value",
      "live_products_pro_spec_3_value",
      "live_products_pro_spec_4_value",
    ] as const,
    ctaKey: "live_products_pro_cta",
    href: "/shop/buy-tactsuit/tactsuit-pro/",
  },
  {
    recommended: false,
    name: "TactSuit Air",
    taglineKey: "live_products_air_tagline",
    points: 16,
    pointsCoverKey: "live_products_air_cover",
    descriptionKey: "live_products_air_desc",
    imageSrc: "/images/software/live/products-air.png",
    mobileImageSrc: "/images/software/live/products-air.png",
    specValues: [
      "live_products_air_spec_1_value",
      "live_products_air_spec_2_value",
      "live_products_air_spec_3_value",
      "live_products_air_spec_4_value",
    ] as const,
    ctaKey: "live_products_air_cta",
    href: "/shop/buy-tactsuit/tactsuit-air/",
  },
];

const SPEC_LABEL_KEYS = [
  "live_products_spec_haptic_points",
  "live_products_spec_battery",
  "live_products_spec_led",
  "live_products_spec_color",
] as const;

function CheckIcon({ recommended }: { recommended: boolean }) {
  return (
    <svg
      className="shrink-0"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
        fill={recommended ? "#0F62FE" : "#21272A"}
      />
    </svg>
  );
}

type ProductCardData = {
  recommended: boolean;
  name: string;
  tagline: string;
  points: number;
  pointsCover: string;
  description: string;
  imageSrc: string;
  mobileImageSrc: string;
  specs: { label: string; value: string }[];
  cta: string;
  href: string;
  compact?: boolean;
};

function ProductCard({
  recommended,
  name,
  tagline,
  points,
  pointsCover,
  description,
  imageSrc,
  mobileImageSrc,
  specs,
  cta,
  href,
  compact = false,
}: ProductCardData) {
  return (
    <div
      className={cn(
        "flex flex-col items-center px-1 pb-1",
        compact ? "w-full" : "flex-[1_0_0]",
        recommended
          ? "rounded-[28px] bg-blue-60"
          : "rounded-[24px] bg-transparent",
      )}
    >
      <div className="flex items-center justify-center py-2">
        <span
          className={cn(
            "text-xs font-600 leading-4 tracking-[0.1px] text-white",
            !recommended && "invisible",
          )}
        >
          RECOMMENDED
        </span>
      </div>

      {/**
       * ★**카드 전체가 링크다** (#5674).
       *
       * 초판은 이미지만 감쌌는데 클릭 가능 면적이 카드의 일부뿐이었다(홈 실측 24%/43%).
       * 목적지가 CTA 와 **같은 상수**(`href`)임을 실측으로 확인했으므로(2026-09-09 · 이
       * 섹션 4카드 전부 일치) 카드 루트를 링크로 올리고 CTA 는 `<span>` 으로 강등했다 —
       * 같은 곳으로 가는 링크가 둘일 이유가 없고, 탭 스톱이 카드당 1개로 줄어든다.
       *
       * 접근 이름은 `aria-label={name}` **하나**다. `<a>` 에 `aria-label` 이 있으면 이름
       * 계산이 자식을 훑지 않으므로 카드 안 제목·스펙 목록이 이름에 섞이지 않는다.
       *
       * ⚠️ `RECOMMENDED` 배지는 이 링크 **밖**에 남긴다 — 카드 위에 붙는 라벨이고
       * 링크 안에 넣으면 이름·클릭 영역이 배지까지 번진다.
       */}
      <Link
        href={href}
        aria-label={name}
        className={cn(
          "group flex w-full flex-col overflow-hidden rounded-[24px] bg-white shadow-[0_8px_16px_0_rgba(0,0,0,0.15)]",
          compact ? "gap-6 p-4" : "gap-8 p-6",
        )}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <h3 className="w-full text-center text-2xl font-400 leading-8 text-coolgray-90">
              {name}
            </h3>
            <p className="w-full text-center text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-70">
              {tagline}
            </p>
          </div>

          <div className="flex w-full items-center justify-center gap-4">
            <span
              className={cn(
                "text-[57px] font-400 leading-[64px]",
                recommended ? "text-blue-60" : "text-coolgray-90",
              )}
            >
              {points}
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-600 leading-[18px] tracking-[0.25px] text-coolgray-90">
                {specs[0]?.label}
              </span>
              <span className="text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-70">
                {pointsCover}
              </span>
            </div>
          </div>
        </div>

        {/* 이미지는 이제 링크가 아니다 — 카드 루트가 링크다(위 주석). 호버는 리포 공용
            `.hover-scale` + 카드 루트의 `group` 결속이고 `laptop:` 은 모바일에 호버가
            없기 때문이다. `alt=""` 는 장식 — 이름은 카드 링크의 `aria-label` 이 갖는다. */}
        <div
          className={cn(
            "relative aspect-square w-full overflow-hidden rounded-2xl",
            compact && !recommended ? "" : "bg-coolgray-10",
          )}
        >
          <Image
            src={compact ? mobileImageSrc : imageSrc}
            alt=""
            fill
            sizes="(max-width: 768px) 280px, 460px"
            className={cn(
              "laptop:hover-scale",
              compact && !recommended ? "object-cover" : "object-contain p-4",
            )}
          />
        </div>

        <p className="w-full whitespace-pre-line text-center text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-90">
          {description}
        </p>

        <ul className="flex flex-col gap-2">
          {specs.map((spec) => (
            <li
              key={spec.label}
              className="flex items-center gap-3 border-b border-coolgray-20 pb-2"
            >
              <CheckIcon recommended={recommended} />
              <span className="min-w-0 flex-1 text-xs font-400 leading-4 tracking-[0.4px] text-coolgray-70">
                {spec.label}
              </span>
              <span className="shrink-0 text-xs font-400 leading-4 tracking-[0.4px] text-coolgray-90">
                {spec.value}
              </span>
            </li>
          ))}
        </ul>

        <Button
          variant={recommended ? "blue-fill" : "darkgray-fill"}
          size="md"
          shape="pill"
          asChild
          className="w-full"
        >
          {/* ★`<span>` 이다 — 카드가 이미 링크라 여기서 `<Link>` 를 쓰면 링크 안 링크가
              된다. `asChild` 를 유지하는 이유: 없으면 `<button>` 이 렌더되고 그건
              `<a>` 안에 올 수 없는 상호작용 요소라 클릭이 깨진다. */}
          <span>{cta}</span>
        </Button>
      </Link>
    </div>
  );
}

// 312px card + 12px gap (gap-3)
const CAROUSEL_STEP = 312 + 12;

function MobileCarousel({ products }: { products: ProductCardData[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeIndexRef = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const next = (activeIndexRef.current + 1) % products.length;
      scrollRef.current?.scrollTo({
        left: next * CAROUSEL_STEP,
        behavior: "smooth",
      });
      activeIndexRef.current = next;
      setActiveIndex(next);
    }, 3000);
    return () => clearInterval(interval);
  }, [products.length]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const index = Math.round(scrollRef.current.scrollLeft / CAROUSEL_STEP);
    const clamped = Math.max(0, Math.min(index, products.length - 1));
    if (clamped !== activeIndexRef.current) {
      setActiveIndex(clamped);
      activeIndexRef.current = clamped;
    }
  };

  return (
    <div className="flex w-full flex-col items-center gap-4">
      {/* pl-4 + scrollPaddingLeft:16px 로 snap 기준점 일치, 오른쪽에 다음 카드 ~20px peek */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex w-full snap-x snap-mandatory gap-3 overflow-x-auto pl-4 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none", scrollPaddingLeft: "16px" }}
      >
        {products.map((p) => (
          <div key={p.name} className="w-[312px] shrink-0 snap-start">
            <ProductCard {...p} compact />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        {products.map((_, i) => (
          <div
            key={i}
            className={cn(
              "size-2 rounded-full transition-colors duration-200",
              i === activeIndex ? "bg-coolgray-50" : "bg-coolgray-20",
            )}
          />
        ))}
      </div>
    </div>
  );
}

export default function LiveProductsSection() {
  const { t } = useTrans(["software"]);
  const specLabels = SPEC_LABEL_KEYS.map((k) => t(k));

  const products: ProductCardData[] = PRODUCTS_KEYS.map((p) => ({
    recommended: p.recommended,
    name: p.name,
    tagline: t(p.taglineKey),
    points: p.points,
    pointsCover: t(p.pointsCoverKey),
    description: t(p.descriptionKey),
    imageSrc: p.imageSrc,
    mobileImageSrc: p.mobileImageSrc,
    specs: specLabels.map((label, i) => ({ label, value: t(p.specValues[i]) })),
    cta: t(p.ctaKey),
    href: p.href,
  }));

  return (
    <ComponentErrorBoundary>
      <section className="relative flex w-full flex-col items-center justify-center gap-6 overflow-hidden md:gap-8 md:py-[96px]">
        <div className="absolute inset-0 hidden md:block">
          <Image
            src="/images/software/live/products-bg.jpg"
            alt=""
            fill
            sizes="100vw"
            className="pointer-events-none object-cover"
          />
        </div>

        <h2 className="relative shrink-0 text-center text-2xl font-400 leading-8 text-coolgray-90 md:text-[32px] md:leading-10">
          {t("live_products_heading")}
        </h2>

        <div className="relative hidden w-full max-w-[952px] items-end gap-6 px-4 md:flex">
          {products.map((p) => (
            <ProductCard key={p.name} {...p} />
          ))}
        </div>

        <div className="relative w-full md:hidden">
          <MobileCarousel products={products} />
        </div>
      </section>
    </ComponentErrorBoundary>
  );
}
